import type {ConversationDispatch} from './reducer';
import type {Edge, AnswerId} from 'src/types/chatbot';
import type {RenderNode} from './conversation-builder';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import invariant from 'invariant';

import {entries} from 'src/utils/object';
import classify from 'src/utils/classify';
import {
  getConditionOperatorLabels,
  isBranchableNodeType,
} from 'src/components/conversation-builder/utils';

import {Menu} from 'src/components/lib/token-list-input/borderless';
import Popover from './popover';
import {Button, UnstyledButton} from 'src/components/lib/new-button';
import {DescriptorText} from './common';
import {ButtonsSmall} from './form';

import css from './condition-selector.css';


export default function ConditionSelector({
  dispatch,
  content,
  edge,
}: {
  dispatch: ConversationDispatch;
  content: RenderNode[];
  edge: Edge;
}) {
  const {t, makeKey} = useLabelTranslation();
  const parentRenderNode = content.find(
    (renderNode) => renderNode.node?.id === edge.from_node_id,
  );

  invariant(
    parentRenderNode && parentRenderNode.branches,
    'Cannot render a condition selector for a non-existent node.',
  );

  // @ts-expect-error - TS2322 - Type 'Set<ConditionValue | null>' is not assignable to type 'Set<AnswerId>'.
  const usedConditions: Set<AnswerId> = new Set(
    parentRenderNode.branches
      .filter((branch) => branch.edge.id !== edge.id)
      .map(({edge}) => edge.conditions)
      .flat(),
  );

  const [selectedConditions, setConditions] = React.useState(
    () => new Set(edge.conditions),
  );

  const [operator, setOperator] = React.useState(edge.operator);

  const {node} = parentRenderNode;

  invariant(
    node && isBranchableNodeType(node),
    'Parent node must allow branching.',
  );

  // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'. | TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
  const answers = node?.answers ? node.answers : [];
  // @ts-expect-error - TS7006 - Parameter 'answer' implicitly has an 'any' type.
  let displayedAnswers = answers.filter((answer) => !answer.disabled);
  // @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
  if (!node?.question_is_multiselect) {
    displayedAnswers = displayedAnswers.filter(
      // @ts-expect-error - TS7006 - Parameter 'answer' implicitly has an 'any' type.
      (answer) => !usedConditions.has(answer.id),
    );
  }

  const handleClose = () => dispatch({type: 'CANCEL_EDIT_EDGE'});

  const conditionOptions = entries(getConditionOperatorLabels(t)).map(
    ([value, label]: [any, any]) => ({value, label: label.toLowerCase()}),
  );

  return (
    <Popover
      onClose={handleClose}
      titleText={t('Select_responses', 'Select responses')}
    >
      <div className={css.body}>
        <DescriptorText className={css.subtitle}>
          {/* @ts-expect-error - TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'. */}
          {node?.question_is_multiselect ? (
            <>
              <span>{t('Response', 'Response')}</span>{' '}
              {false ? (
                <Menu
                  classNames={{container: css.operatorMenu}}
                  value={operator || 'every'}
                  options={conditionOptions}
                  onChange={(option) => setOperator(option.value)}
                />
              ) : (
                <span>{t('contains', 'contains')}</span>
              )}
            </>
          ) : (
            <>
              {`${t('Select_from', 'Select from')} `}
              <strong>{answers.length}</strong>
              {` ${t('accepted_answers', 'accepted answers')}:`}
            </>
          )}
        </DescriptorText>

        <div className={css.conditions}>
          {/* @ts-expect-error - TS7006 - Parameter 'answer' implicitly has an 'any' type. */}
          {displayedAnswers.map((answer) => (
            <ConditionToken
              key={answer.id}
              isSelected={selectedConditions.has(answer.id)}
              onClick={() => {
                const nextConditions = new Set(selectedConditions);
                if (selectedConditions.has(answer.id)) {
                  nextConditions.delete(answer.id);
                } else {
                  nextConditions.add(answer.id);
                }
                setConditions(nextConditions);
              }}
            >
              {answer.label}
            </ConditionToken>
          ))}
        </div>

        <ButtonsSmall className={css.buttons}>
          <Button size="medium" onClick={handleClose}>
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() =>
              dispatch({
                type: 'SUBMIT_EDIT_EDGE',
                payload: {
                  conditions: [...selectedConditions],
                  operator,
                },
              })
            }
          >
            {t('Done', 'Done')}
          </Button>
        </ButtonsSmall>
      </div>
    </Popover>
  );
}

// @ts-expect-error - TS7031 - Binding element 'isSelected' implicitly has an 'any' type.
function ConditionToken({isSelected, ...props}) {
  return (
    <UnstyledButton
      className={classify(css.token, isSelected && css.selected)}
      {...props}
    />
  );
}
